import { Box, Button, Stack, Typography } from "@mui/material";
import { RHFTextField } from "../../../../components/hook-form";
import { useFormContext } from "react-hook-form";
import { IExaminationSchema } from "../../../../@types/examination";

type Props = {
  clinicalRecordId: number;
  setExamination: (data: IExaminationSchema) => void;
};

const OMB = ({ clinicalRecordId, setExamination }: Props) => {
  const { setValue, getValues } = useFormContext();

  const clearExternalData = () => {
    const fieldNames = [
      "ocularMotorBalance.leftNPC",
      "ocularMotorBalance.leftAccommodation",
      "ocularMotorBalance.leftDistanceCTWithRX",
      "ocularMotorBalance.leftDistanceCTWithoutRX",
      "ocularMotorBalance.leftNearCTWithRX",
      "ocularMotorBalance.leftNearCTWithoutRX",
      "ocularMotorBalance.leftMotility",
      "ocularMotorBalance.leftConvergence",
      "ocularMotorBalance.leftMaddoxRod",
      "ocularMotorBalance.leftAdditionalNotes",
      "ocularMotorBalance.leftStereopsis",
      "ocularMotorBalance.rightNPC",
      "ocularMotorBalance.rightAccommodation",
      "ocularMotorBalance.rightDistanceCTWithRX",
      "ocularMotorBalance.rightDistanceCTWithoutRX",
      "ocularMotorBalance.rightNearCTWithRX",
      "ocularMotorBalance.rightNearCTWithoutRX",
      "ocularMotorBalance.rightMotility",
      "ocularMotorBalance.rightConvergence",
      "ocularMotorBalance.rightMaddoxRod",
      "ocularMotorBalance.rightAdditionalNotes",
      "ocularMotorBalance.rightStereopsis",
    ];

    const clearedValues = { ...getValues() };
    fieldNames.forEach((fieldName) => {
      clearedValues[fieldName] = "";
    });

    // Update the form values
    Object.keys(clearedValues).forEach((fieldName) => {
      setValue(fieldName, clearedValues[fieldName]);
    });
  };

  const copyData = () => {
    const leftNPC = getValues("ocularMotorBalance.leftNPC");
    const leftAccommodation = getValues("ocularMotorBalance.leftAccommodation");
    const leftDistanceCTWithRX = getValues(
      "ocularMotorBalance.leftDistanceCTWithRX"
    );
    const leftDistanceCTWithoutRX = getValues(
      "ocularMotorBalance.leftDistanceCTWithoutRX"
    );
    const leftNearCTWithRX = getValues("ocularMotorBalance.leftNearCTWithRX");
    const leftNearCTWithoutRX = getValues(
      "ocularMotorBalance.leftNearCTWithoutRX"
    );
    const leftMotility = getValues("ocularMotorBalance.leftMotility");
    const leftConvergence = getValues("ocularMotorBalance.leftConvergence");
    const leftMaddoxRod = getValues("ocularMotorBalance.leftMaddoxRod");
    const leftAdditionalNotes = getValues(
      "ocularMotorBalance.leftAdditionalNotes"
    );
    const leftStereopsis = getValues("ocularMotorBalance.leftStereopsis");

    setValue("ocularMotorBalance.rightNPC", leftNPC);
    setValue("ocularMotorBalance.rightAccommodation", leftAccommodation);
    setValue("ocularMotorBalance.rightStereopsis", leftStereopsis);
    setValue("ocularMotorBalance.rightDistanceCTWithRX", leftDistanceCTWithRX);
    setValue(
      "ocularMotorBalance.rightDistanceCTWithoutRX",
      leftDistanceCTWithoutRX
    );
    setValue("ocularMotorBalance.rightNearCTWithRX", leftNearCTWithRX);
    setValue("ocularMotorBalance.rightNearCTWithoutRX", leftNearCTWithoutRX);
    setValue("ocularMotorBalance.rightMotility", leftMotility);
    setValue("ocularMotorBalance.rightConvergence", leftConvergence);
    setValue("ocularMotorBalance.rightMaddoxRod", leftMaddoxRod);
    setValue("ocularMotorBalance.rightAdditionalNotes", leftAdditionalNotes);
  };

  const setDefaultValue = () => {
    setValue("ocularMotorBalance.rightNPC", "5cm");
    setValue("ocularMotorBalance.rightAccommodation", "normal");
    setValue("ocularMotorBalance.rightStereopsis", "normal");
    setValue("ocularMotorBalance.rightDistanceCTWithRX", "NMD");
    setValue("ocularMotorBalance.rightDistanceCTWithoutRX", "NMD");
    setValue("ocularMotorBalance.rightNearCTWithRX", "NMD");
    setValue("ocularMotorBalance.rightNearCTWithoutRX", "NMD");
    setValue("ocularMotorBalance.rightMotility", "full");
    setValue("ocularMotorBalance.rightConvergence", "within normal limits");
    setValue("ocularMotorBalance.rightMaddoxRod", "no slip");
    setValue("ocularMotorBalance.rightAdditionalNotes", "");
    setValue("ocularMotorBalance.leftNPC", "5cm");
    setValue("ocularMotorBalance.leftAccommodation", "normal");
    setValue("ocularMotorBalance.leftStereopsis", "normal");
    setValue("ocularMotorBalance.leftDistanceCTWithRX", "NMD");
    setValue("ocularMotorBalance.leftDistanceCTWithoutRX", "NMD");
    setValue("ocularMotorBalance.leftNearCTWithRX", "NMD");
    setValue("ocularMotorBalance.leftNearCTWithoutRX", "NMD");
    setValue("ocularMotorBalance.leftMotility", "full");
    setValue("ocularMotorBalance.leftConvergence", "within normal limits");
    setValue("ocularMotorBalance.leftMaddoxRod", "no slip");
    setValue("ocularMotorBalance.leftAdditionalNotes", "");
  };

  return (
    <>
      <Stack
        direction="row"
        alignItems="right"
        justifyContent="right"
        ml={1}
        mr={1}
        spacing={2}>
        <Button variant="contained" onClick={clearExternalData}>
          Clear
        </Button>
        <Button variant="contained" onClick={setDefaultValue}>
          Default
        </Button>
        <Button variant="contained" onClick={copyData}>
          Copy R to L
        </Button>
      </Stack>
      <Box
        sx={{
          display: "flex",
          width: "100%",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
        p={5}>
        <Box
          sx={{
            width: "33.33%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}>
          <Typography color={"#637381"} variant="h6" mb={3}>
            Right
          </Typography>
          <RHFTextField
            name="ocularMotorBalance.leftNPC"
            size="small"
            sx={{ width: "90%", marginBottom: "10px" }}
          />
          <RHFTextField
            name="ocularMotorBalance.leftAccommodation"
            size="small"
            sx={{ width: "90%", marginBottom: "10px" }}
          />
          <RHFTextField
            name="ocularMotorBalance.leftDistanceCTWithRX"
            size="small"
            sx={{ width: "90%", marginBottom: "10px" }}
          />
          <RHFTextField
            name="ocularMotorBalance.leftDistanceCTWithoutRX"
            size="small"
            sx={{ width: "90%", marginBottom: "10px" }}
          />
          <RHFTextField
            name="ocularMotorBalance.leftNearCTWithRX"
            size="small"
            sx={{ width: "90%", marginBottom: "10px" }}
          />
          <RHFTextField
            name="ocularMotorBalance.leftNearCTWithoutRX"
            size="small"
            sx={{ width: "90%", marginBottom: "10px" }}
          />
          <RHFTextField
            name="ocularMotorBalance.leftStereopsis"
            size="small"
            sx={{ width: "90%", marginBottom: "10px" }}
          />
          <RHFTextField
            name="ocularMotorBalance.leftMotility"
            size="small"
            sx={{ width: "90%", marginBottom: "10px" }}
          />
          <RHFTextField
            name="ocularMotorBalance.leftConvergence"
            size="small"
            sx={{ width: "90%", marginBottom: "10px" }}
          />
          <RHFTextField
            name="ocularMotorBalance.leftMaddoxRod"
            size="small"
            sx={{ width: "90%", marginBottom: "10px" }}
          />
          <RHFTextField
            name="ocularMotorBalance.leftAdditionalNotes"
            size="small"
            multiline
            rows={4}
            sx={{ width: "90%", marginBottom: "10px" }}
          />
        </Box>
        <Box
          sx={{
            marginBottom: "50px",
            width: "33.33%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}>
          <Typography color={"#637381"} variant="h6" mb={3}></Typography>
          <Typography color={"#637381"} variant="h6" mt={3}>
            NPC
          </Typography>
          <Typography color={"#637381"} variant="h6" mt={3}>
            Accommodation
          </Typography>
          <Typography color={"#637381"} variant="h6" mt={3}>
            Distance CT with RX
          </Typography>
          <Typography color={"#637381"} variant="h6" mt={3}>
            Distance CT without RX
          </Typography>
          <Typography color={"#637381"} variant="h6" mt={3}>
            Near CT with RX
          </Typography>
          <Typography color={"#637381"} variant="h6" mt={3}>
            Near CT without RX
          </Typography>
          <Typography color={"#637381"} variant="h6" mt={3}>
            Stereopsis
          </Typography>
          <Typography color={"#637381"} variant="h6" mt={3}>
            Motility
          </Typography>
          <Typography color={"#637381"} variant="h6" mt={3}>
            Convergence
          </Typography>
          <Typography color={"#637381"} variant="h6" mt={3}>
            Maddox Rod
          </Typography>
          <Typography color={"#637381"} variant="h6" mt={6}>
            Additional Notes
          </Typography>
        </Box>
        <Box
          sx={{
            width: "33.33%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}>
          <Typography color={"#637381"} variant="h6" mb={3}>
            Left
          </Typography>
          <RHFTextField
            name="ocularMotorBalance.rightNPC"
            size="small"
            sx={{ width: "90%", marginBottom: "10px" }}
          />
          <RHFTextField
            name="ocularMotorBalance.rightAccommodation"
            size="small"
            sx={{ width: "90%", marginBottom: "10px" }}
          />
          <RHFTextField
            name="ocularMotorBalance.rightDistanceCTWithRX"
            size="small"
            sx={{ width: "90%", marginBottom: "10px" }}
          />
          <RHFTextField
            name="ocularMotorBalance.rightDistanceCTWithoutRX"
            size="small"
            sx={{ width: "90%", marginBottom: "10px" }}
          />
          <RHFTextField
            name="ocularMotorBalance.rightNearCTWithRX"
            size="small"
            sx={{ width: "90%", marginBottom: "10px" }}
          />
          <RHFTextField
            name="ocularMotorBalance.rightNearCTWithoutRX"
            size="small"
            sx={{ width: "90%", marginBottom: "10px" }}
          />
          <RHFTextField
            name="ocularMotorBalance.rightStereopsis"
            size="small"
            sx={{ width: "90%", marginBottom: "10px" }}
          />
          <RHFTextField
            name="ocularMotorBalance.rightMotility"
            size="small"
            sx={{ width: "90%", marginBottom: "10px" }}
          />
          <RHFTextField
            name="ocularMotorBalance.rightConvergence"
            size="small"
            sx={{ width: "90%", marginBottom: "10px" }}
          />
          <RHFTextField
            name="ocularMotorBalance.rightMaddoxRod"
            size="small"
            sx={{ width: "90%", marginBottom: "10px" }}
          />
          <RHFTextField
            name="ocularMotorBalance.rightAdditionalNotes"
            size="small"
            multiline
            rows={4}
            sx={{ width: "90%", marginBottom: "1px" }}
          />
        </Box>
      </Box>
    </>
  );
};

export default OMB;
